import { taskReleaseLabel } from "../config/main";
import { mockRequestFellowTrials } from "./fellowMockServer";
import { postFellow } from "./fellowClient";
import { saveAdaptiveFlowLatency } from "./latencies/adaptiveFlowLogStore";

const FELLOW_DEFAULT_BASE_URL = "https://backend2.bgbehavior.com";

function getFellowBaseUrl() {
  return (process.env.REACT_APP_FELLOW_BASE_URL || FELLOW_DEFAULT_BASE_URL).replace(/\/+$/, "");
}

function nowMs() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

// Same flag App.jsx uses to pick the local missionIsAdaptive test config (npm run
// dev:0:rl:browser) - reused here so that same local run stays on the mock,
// while every other run (including deploy) hits the real backend.
export function shouldUseMockBackend() {
  return process.env.REACT_APP_RL_EXPERIMENT === "true";
}

function buildBlockRequestPayload({ participantID, startDate, missionNumber, block, trialNumber }) {
  return {
    gf_version: taskReleaseLabel || "",
    participant_id: participantID || "unknown",
    start_date: startDate,
    missionNumber,
    block,
    trialNumber,
    build_block: true,
  };
}

// Shared POST /fellow `build_block` request, used by both functions below.
async function sendBuildBlockRequest(payload) {
  const usingMock = shouldUseMockBackend();
  const baseUrl = usingMock ? null : getFellowBaseUrl();
  const requestStartMs = nowMs();
  let response;
  try {
    response = usingMock ? await mockRequestFellowTrials(payload) : await postFellow(baseUrl, payload);
  } catch (error) {
    // Tag the error with the request that triggered it, so callers logging
    // the failure (see adaptiveMissionTrials.js) can record exactly what was
    // sent - base URL, payload, etc.
    error.payload = payload;
    error.baseUrl = baseUrl;
    throw error;
  }

  if (!usingMock) {
    saveAdaptiveFlowLatency({
      timestamp: new Date().toISOString(),
      rtt_ms: Math.round(nowMs() - requestStartMs),
      payload,
    });
  }

  return { response, baseUrl };
}

/**
 * Requests the next block of adaptive trials for a mission, using the request
 * payload shape documented for the real POST /fellow `build_block` path. Uses
 * the local mock (`fellowMockServer.js`) when running the local RL/adaptive
 * test setup (`npm run dev:0:rl:browser`), so that setup stays reproducible
 * without depending on live backend state - every other run (including
 * deploy) hits the real `/fellow` endpoint directly via `postFellow`.
 *
 * @param {Object} options
 * @param {string} options.participantID
 * @param {string} options.startDate ISO timestamp, as stored in jsPsych's global data
 * @param {number} options.missionNumber
 * @param {number} options.block Block being requested (must be a positive integer)
 * @param {number} options.trialNumber Trial number to resume numbering from
 * @returns {Promise<Array>} The batch of trial configs returned by the backend (mock or real)
 */
export async function requestNextAdaptiveTrials({
  participantID,
  startDate,
  missionNumber,
  block,
  trialNumber,
}) {
  const payload = buildBlockRequestPayload({ participantID, startDate, missionNumber, block, trialNumber });
  const { response, baseUrl } = await sendBuildBlockRequest(payload);

  const trials = response?.timeline;

  if (!Array.isArray(trials) || trials.length === 0) {
    const error = new Error("Adaptive trials request returned no trials");
    error.payload = payload;
    error.baseUrl = baseUrl;
    throw error;
  }

  return trials;
}

/**
 * Fire-and-forget POST sent after the mission's terminal (blockType 98) trial
 * renders, so the backend's data plane sees its Firestore doc and flags the
 * mission complete. Unlike requestNextAdaptiveTrials, an empty/missing
 * `timeline` reply is expected here, not an error, and failures just log.
 */
export async function notifyAdaptiveMissionComplete({
  participantID,
  startDate,
  missionNumber,
  block,
  trialNumber,
}) {
  const payload = buildBlockRequestPayload({ participantID, startDate, missionNumber, block, trialNumber });
  try {
    await sendBuildBlockRequest(payload);
  } catch (error) {
    console.error("[AdaptiveTrials] Mission-complete notification failed:", error);
  }
}
